import { z } from "zod";

export const PHOTON_IDENTIFIER_SYSTEM_PRESCRIPTIONS = "photon.prescriptions";
export const PHOTON_IDENTIFIER_SYSTEM_ORDERS = "photon.orders";
export const PHOTON_IDENTIFIER_SYSTEM_PHARMACIES = "photon.pharmacies";
export const PHOTON_IDENTIFIER_SYSTEM_MEDICATIONS = "photon.medications";
export const PHOTON_IDENTIFIER_SYSTEM_PRESCRIBERS = "photon.prescribers";

export const PHOTON_EXTENSION_URL_FULFILLMENT_TYPE = "photon.fulfillment-type";

// Base interface with common fields
export interface PhotonPatientBase {
  externalId?: string;
  name: PhotonNameInput;
  dateOfBirth: string; // Format: YYYY-MM-DD
  sex: "MALE" | "FEMALE" | "UNKNOWN";
  gender?: string;
  phone: string;
  allergies?: PhotonAllergenInput[];
  medicationHistory?: PhotonMedHistoryInput[];
  address?: PhotonAddressInput;
  preferredPharmacies?: string[];
}

// Creation interface with required fields per API spec
export type PhotonPatientCreateInput = PhotonPatientBase;

// Update interface with all fields optional except id
export interface PhotonPatientUpdateInput extends Partial<Omit<PhotonPatientBase, "id">> {
  id: string; // Required for updates
  // Only the email-change job sends this. The patient sync never does: Photon reaches the
  // patient by SMS, so the email is a disclosure with no use on the prescribing path.
  email?: string;
}

export interface PhotonNameInput {
  title?: string;
  first: string;
  middle?: string;
  last: string;
}

export interface PhotonAllergenInput {
  allergenId: string;
  comment?: string;
  onset?: string;
}

export interface PhotonMedHistoryInput {
  medicationId: string;
  active: boolean;
  comment?: string;
}

export interface PhotonAddressInput {
  name?: PhotonNameInput;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface PhotonAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface PhotonPatientResponse {
  id: string;
  externalId?: string;
  name: {
    first: string;
    last: string;
  };
  dateOfBirth: string;
  sex: string;
  phone: string;
  email?: string;
  address?: PhotonAddress;
  allergies?: {
    allergen: {
      id: string;
      name: string;
      rxcui?: string;
    };
    comment?: string;
    onset?: string;
  }[];
  medicationHistory?: {
    active: boolean;
    comment?: string;
    medication: {
      id: string;
      name: string;
      genericName?: string;
      brandName?: string;
      strength?: string;
      form?: string;
    };
  }[];
  preferredPharmacies?: PhotonPharmacyResponse[];
}

export interface PhotonPharmacyResponse {
  id: string;
  name: string;
  name2?: string;
  NPI?: string;
  NCPDP?: string;
  address?: PhotonAddress;
  fax?: string;
  phone?: string;
  fulfillmentTypes?: string[];
}

export interface PhotonAllergenResponse {
  id: string;
  name: string;
  rxcui: string;
}

export const PhotonMedicationSchema = z.object({
  id: z.string(),
  name: z.string(),
  codes: z.object({
    rxcui: z.string().nullable(),
    productNDC: z.string().nullable(),
    packageNDC: z.string().nullable(),
    SKU: z.string().nullable(),
    HCPCS: z.string().nullable(),
  }),
  type: z.enum(["RX", "OTC"]).nullable(),
  concept: z.enum(["DRUG", "PRODUCT", "PACKAGE"]),
  schedule: z.enum(["I", "II", "III", "IV", "V"]).nullable(),
  controlled: z.boolean(),
  brandName: z.string().nullable(),
  genericName: z.string().nullable(),
  strength: z.string().nullable(),
  form: z.string().nullable(),
  manufacturer: z.string().nullable(),
  description: z.string().nullable(),
});

export type PhotonMedication = z.infer<typeof PhotonMedicationSchema>;

export const PhotonMedicationResponseSchema = z.object({
  medications: z.array(PhotonMedicationSchema).default([]),
});

export type PhotonMedicationResponse = z.infer<typeof PhotonMedicationResponseSchema>;

export const PhotonTreatmentOptionSchema = z.object({
  __typename: z.string().optional(),
  id: z.string(),
  name: z.string(),
  rxcui: z.string().optional(),
  brandName: z.string().optional(),
});

export type PhotonTreatmentOption = z.infer<typeof PhotonTreatmentOptionSchema>;

export const PhotonTreatmentSearchResponseSchema = z.object({
  treatments: z.array(PhotonTreatmentOptionSchema).default([]),
  unavailableReason: z.literal("practice-not-configured").optional(),
});

export type PhotonTreatmentSearchResponse = z.infer<typeof PhotonTreatmentSearchResponseSchema>;

export interface PhotonProviderResponse {
  id: string;
  externalId?: string;
  name: {
    first: string;
    last: string;
  };
  email: string;
  phone: string;
  fax?: string;
  address?: PhotonAddressInput; // Assuming address input is okay here based on docs
  NPI?: string;
}

export interface PhotonDiagnosisResponse {
  id: string;
  code: string;
  type: string; // e.g., "ICD10"
  description?: string;
}

export interface PhotonTreatmentResponse {
  id: string;
  name: string;
  codes: {
    rxcui?: string;
    productNDC?: string;
    packageNDC?: string;
    SKU?: string;
    HCPCS?: string;
  };
  description?: string;
  // Include other fields if TreatmentFragment has more details than MedicationResponse
  // For now, assuming it's similar enough to PhotonMedicationResponse structure
}

export interface PhotonFillResponse {
  id: string;
  state: string; // e.g., "SCHEDULED", "FILLED"
  requestedAt?: string; // AWSDateTime
  filledAt?: string; // AWSDateTime
  // Include other relevant fields if FillFragment has them
}

export interface PhotonPrescriptionResponse {
  id: string;
  externalId?: string;
  prescriber: PhotonProviderResponse;
  patient: PhotonPatientResponse; // Assuming existing PhotonPatientResponse is sufficient
  state: "ACTIVE" | "EXPIRED" | "DEPLETED" | "CANCELED";
  treatment: PhotonTreatmentResponse;
  dispenseAsWritten: boolean;
  dispenseQuantity: number;
  dispenseUnit: string;
  refillsAllowed: number;
  refillsRemaining: number;
  fillsAllowed: number;
  fillsRemaining: number;
  daysSupply?: number;
  instructions: string;
  notes?: string;
  diagnoses?: PhotonDiagnosisResponse[];
  effectiveDate: string; // AWSDate
  expirationDate: string; // AWSDate
  writtenAt: string; // AWSDateTime
  fills: PhotonFillResponse[];
}

/** Pharmacy postal address as Photon returns it. */
export const PhotonAddressSchema = z.object({
  street1: z.string(),
  street2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.string().nullable().optional(),
});

export type PhotonPharmacyAddress = z.infer<typeof PhotonAddressSchema>;

// Schema for the response of the Photon 'order' query
export const PhotonOrderResponseSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  patient: z.object({
    id: z.string(),
    externalId: z.string().nullable(),
  }),
  pharmacyId: z.string().nullable().optional(), // Pharmacy might not be assigned yet
  pharmacyName: z.string().nullable().optional(), // Human-readable pharmacy name, when assigned
  pharmacyPhone: z.string().nullable().optional(),
  pharmacyAddress: PhotonAddressSchema.nullable().optional(),
  fills: z.array(
    z.object({
      id: z.string(),
      prescription: z.object({
        id: z.string(),
        externalId: z.string().nullable(),
      }),
      treatment: z.object({
        id: z.string(),
        name: z.string(),
        // Add other relevant treatment fields if needed from TreatmentFragment
      }),
      // Add other relevant fill fields if needed from FillFragment
    }),
  ),
  // Add other relevant order fields if needed from OrderFragment
});

export type PhotonOrderResponse = z.infer<typeof PhotonOrderResponseSchema>;

// Common fields for all webhook events
const BasePhotonWebhookSchema = z.object({
  id: z.string(),
  specversion: z.string(),
  datacontenttype: z.string(),
  time: z.string(),
  subject: z.string(),
  source: z.string(),
});

// Patient info schema used in multiple events
const PhotonPatientSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
});

export const PrescriptionCreatedSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string().describe("Internal Photon ID for the prescription"),
    externalId: z
      .string()
      .nullable()
      .describe("External ID passed in during prescription creation"),
    dispenseQuantity: z.number().describe("Amount of a dispenseUnit per fill"),
    dispenseUnit: z.string().describe("Unit to dispense medication in"),
    dispenseAsWritten: z.boolean().describe("True if substitutes are not allowed at the pharmacy"),
    refillsAllowed: z.number().describe("Number of refills allowed before expiration of script"),
    daysSupply: z.number().describe("Number of days a single fill lasts"),
    instructions: z.string().describe("Prescriber instructions for patient"),
    notes: z.string().describe("Prescriber notes, available to pharmacist"),
    effectiveDate: z.string().describe("Date when the script is first valid"),
    expirationDate: z.string().describe("Date when the script is no longer valid"),
    prescriberId: z.string().describe("ID of the prescriber"),
    treatmentId: z.string().describe("ID of the medication being prescribed"),
    patient: PhotonPatientSchema,
  }),
});

export const PrescriptionDepletedSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string().describe("Internal Photon ID for the prescription"),
    patient: PhotonPatientSchema,
  }),
});

export const PrescriptionActiveSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string().describe("Internal Photon ID for the prescription"),
    patient: PhotonPatientSchema,
  }),
});

export const PrescriptionExpiredSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string().describe("Internal Photon ID for the prescription"),
    patient: PhotonPatientSchema,
  }),
});

export type PrescriptionCreatedEvent = z.infer<typeof PrescriptionCreatedSchema>;
export type PrescriptionActiveEvent = z.infer<typeof PrescriptionActiveSchema>;
export type PrescriptionExpiredEvent = z.infer<typeof PrescriptionExpiredSchema>;

// Schemas for different event types
export const OrderCreatedSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string(),
    externalId: z.string().nullable(),
    pharmacyId: z.string().nullable(),
    patient: PhotonPatientSchema,
    fills: z.array(
      z.object({
        id: z.string(),
        prescription: z.object({
          id: z.string(),
          externalId: z.string().nullable(),
        }),
      }),
    ),
  }),
});

export type OrderCreatedEvent = z.infer<typeof OrderCreatedSchema>;

export const OrderPlacedSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string(),
    externalId: z.string().nullable(),
    patient: PhotonPatientSchema,
  }),
});

export type OrderPlacedEvent = z.infer<typeof OrderPlacedSchema>;

export const OrderFulfillmentSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string(),
    externalId: z.string().nullable(),
    fulfillment: z.object({
      type: z.enum(["MAIL_ORDER", "PICK_UP"]),
      state: z.enum(["SENT", "FILLING", "SHIPPED", "DELIVERED", "RECEIVED", "READY", "PICKED_UP"]),
      tracking: z
        .object({
          url: z.string().nullable(),
          number: z.string().nullable(),
          carrier: z.string().nullable(),
        })
        .nullish(),
    }),
    patient: PhotonPatientSchema,
  }),
});

export type OrderFulfillmentEvent = z.infer<typeof OrderFulfillmentSchema>;

export const OrderReroutedSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string(),
    externalId: z.string().nullable(),
    pharmacy: z.object({
      id: z.string(),
      name: z.string(),
      address: z.object({
        street1: z.string(),
        street2: z.string().nullable(),
        city: z.string(),
        state: z.string(),
        country: z.string(),
        postalCode: z.string(),
      }),
    }),
    patient: PhotonPatientSchema,
  }),
});

export type OrderReroutedEvent = z.infer<typeof OrderReroutedSchema>;

export const OrderCompletedSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string(),
    externalId: z.string().nullable(),
    patient: PhotonPatientSchema,
  }),
});

export type OrderCompletedEvent = z.infer<typeof OrderCompletedSchema>;

export const OrderCanceledSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string(),
    externalId: z.string().nullable(),
    patient: PhotonPatientSchema,
  }),
});

export type OrderCanceledEvent = z.infer<typeof OrderCanceledSchema>;

export const OrderErrorSchema = BasePhotonWebhookSchema.extend({
  data: z.object({
    id: z.string(),
    externalId: z.string().nullable(),
    reason: z.string(),
    patient: PhotonPatientSchema,
  }),
});

export type OrderErrorEvent = z.infer<typeof OrderErrorSchema>;

// Combined schema for all possible webhook events
export const PhotonWebhookSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("photon:prescription:created"), ...PrescriptionCreatedSchema.shape }),
  z.object({
    type: z.literal("photon:prescription:depleted"),
    ...PrescriptionDepletedSchema.shape,
  }),
  z.object({
    type: z.literal("photon:prescription:active"),
    ...PrescriptionActiveSchema.shape,
  }),
  z.object({
    type: z.literal("photon:prescription:expired"),
    ...PrescriptionExpiredSchema.shape,
  }),
  z.object({ type: z.literal("photon:order:created"), ...OrderCreatedSchema.shape }),
  z.object({ type: z.literal("photon:order:placed"), ...OrderPlacedSchema.shape }),
  z.object({ type: z.literal("photon:order:fulfillment"), ...OrderFulfillmentSchema.shape }),
  z.object({ type: z.literal("photon:order:rerouted"), ...OrderReroutedSchema.shape }),
  z.object({ type: z.literal("photon:order:completed"), ...OrderCompletedSchema.shape }),
  z.object({ type: z.literal("photon:order:canceled"), ...OrderCanceledSchema.shape }),
  z.object({ type: z.literal("photon:order:error"), ...OrderErrorSchema.shape }),
]);

//-----------------------------------------------------------------------------------------------
//                             PRESCRIPTION CREATION (INPUT TYPES)
//-----------------------------------------------------------------------------------------------

export interface PhotonCreatePrescriptionDiagnosisInput {
  type: "ICD10";
  code: string;
  name: string;
}

export interface PhotonCreatePrescriptionInput {
  externalId?: string;
  patientId: string;
  treatmentId: string;
  dispenseAsWritten: boolean;
  dispenseQuantity: number;
  dispenseUnit: string;
  daysSupply?: number;
  instructions: string;
  notes?: string;
  effectiveDate: string; // AWSDate (YYYY-MM-DD)
  diagnoses?: PhotonCreatePrescriptionDiagnosisInput[];
}
